/**
 * Plan-feature lookups for the UI.
 *
 * The authority is the backend: `backend/app/core/plan_gating.py`'s
 * `PLAN_FEATURES` decides which tier unlocks what, and every gate is
 * re-checked server-side. This module never carries its own copy of that
 * table — it reads the list the API hands over (`plan.plan_features` on a
 * plan card, `authStore.planStatus.features` for the signed-in account) and
 * only owns the *words* shown next to each key.
 *
 * Everything here is UX: it decides whether a node is offered with a lock,
 * not whether it runs.
 */

/** Feature keys this UI knows how to talk about. */
export type PlanFeatureKey =
  | 'sso'
  | 'team_invites'
  | 'ab_testing'
  | 'deck_generation'
  | 'crm_auto_sync'
  | 'team_alerts'
  | 'per_user_credit_budgets'
  // Settings > Signatures: a personal signature, auto-applied at launch and
  // on any mailbox you have access to (mailbox signatures stay available
  // on every plan).
  | 'per_user_signatures'

/**
 * The label shown for each capability, and whether it earns a row on the plan
 * card.
 *
 * `PLAN_FEATURES` also registers `phone_enrichment`, `job_change_signals`,
 * `custom_roles` and `custom_analytics`. They are deliberately absent here:
 * nothing in the product implements them yet, so a card row would advertise a
 * feature a customer cannot find (see BILLING_WIRED_UP_FEATURES.md). Add the
 * entry in the same change that adds the feature.
 */
export const PLAN_FEATURE_LABELS: Record<PlanFeatureKey, string> = {
  sso: 'SSO (SAML / OIDC)',
  team_invites: 'Invite teammates',
  ab_testing: 'A/B testing',
  deck_generation: 'AI deck generation',
  crm_auto_sync: 'CRM auto-sync',
  team_alerts: 'Team alerts (Slack / Teams)',
  per_user_credit_budgets: 'Per-user credit budgets + audit log',
  per_user_signatures: 'Personal signatures',
}

/**
 * The capability rows a plan card lists, in the order the pricing table reads
 * them. Every key must exist in PLAN_FEATURE_LABELS.
 */
export const PLAN_CARD_FEATURES: PlanFeatureKey[] = [
  'ab_testing',
  'deck_generation',
  'crm_auto_sync',
  'team_alerts',
  'team_invites',
  'per_user_credit_budgets',
  'sso',
]

/** Whether `features` (as returned by the API) unlocks `key`. */
export function hasPlanFeature(features: string[] | null | undefined, key: PlanFeatureKey): boolean {
  return Array.isArray(features) && features.includes(key)
}

/**
 * What to tell somebody who just hit a lock.
 *
 * Deliberately names no tier. Which plan unlocks a feature is data the
 * backend owns and the Billing page already renders per-card from the API;
 * repeating "Momentum or Enterprise" here would be a second copy of
 * PLAN_FEATURES in TypeScript, stale the first time a feature moves tier —
 * and a wrong tier name in an upgrade prompt is worse than none.
 */
export function planFeatureUpgradeHint(key: PlanFeatureKey): string {
  const label = PLAN_FEATURE_LABELS[key] ?? 'This step'
  return `${label} is not included in your plan. Compare plans in Billing to unlock it.`
}
